import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Plus, Edit2, Trash2, CheckCircle, XCircle, RefreshCw, Download } from 'lucide-react';

interface SubscriptionPlan {
  id: string;
  name: string;
  stripe_price_id: string;
  amount: number;
  currency: string;
  interval: 'month' | 'year';
  active: boolean;
  created_at: string;
}

interface StripePrice {
  id: string;
  unit_amount: number | null;
  currency: string;
  recurring: {
    interval: string;
    interval_count: number;
  } | null;
  active: boolean;
}

interface StripeProduct {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  prices: StripePrice[];
}

export function PlansManagement() {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [stripeProducts, setStripeProducts] = useState<StripeProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingStripe, setLoadingStripe] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [activeTab, setActiveTab] = useState<'local' | 'stripe'>('stripe');
  const [formData, setFormData] = useState({
    name: '',
    stripe_price_id: '',
    amount: '',
    interval: 'month' as 'month' | 'year',
    active: true,
  });

  useEffect(() => {
    fetchPlans();
    fetchStripeProducts();
  }, []);

  async function fetchPlans() {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('amount', { ascending: true });

      if (error) throw error;
      setPlans(data || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchStripeProducts() {
    setLoadingStripe(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-stripe-products`;
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch Stripe products');

      const { products } = await response.json();
      setStripeProducts(products || []);
    } catch (error) {
      console.error('Error fetching Stripe products:', error);
    } finally {
      setLoadingStripe(false);
    }
  }

  function resetForm() {
    setFormData({
      name: '',
      stripe_price_id: '',
      amount: '',
      interval: 'month',
      active: true,
    });
    setEditingPlan(null);
    setShowForm(false);
  }

  function handleEdit(plan: SubscriptionPlan) {
    setFormData({
      name: plan.name,
      stripe_price_id: plan.stripe_price_id,
      amount: (plan.amount / 100).toString(),
      interval: plan.interval,
      active: plan.active,
    });
    setEditingPlan(plan);
    setShowForm(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    try {
      const planData = {
        name: formData.name,
        stripe_price_id: formData.stripe_price_id,
        amount: Math.round(parseFloat(formData.amount) * 100),
        currency: 'EUR',
        interval: formData.interval,
        active: formData.active,
      };

      if (editingPlan) {
        const { error } = await supabase
          .from('subscription_plans')
          .update({
            ...planData,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingPlan.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('subscription_plans')
          .insert(planData);

        if (error) throw error;
      }

      await fetchPlans();
      resetForm();
    } catch (error) {
      console.error('Error saving plan:', error);
      alert('Erreur lors de la sauvegarde du plan');
    }
  }

  async function handleDelete(planId: string) {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce plan ?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('subscription_plans')
        .delete()
        .eq('id', planId);

      if (error) throw error;
      await fetchPlans();
    } catch (error) {
      console.error('Error deleting plan:', error);
      alert('Erreur lors de la suppression du plan');
    }
  }

  async function toggleActive(planId: string, currentActive: boolean) {
    try {
      const { error } = await supabase
        .from('subscription_plans')
        .update({
          active: !currentActive,
          updated_at: new Date().toISOString(),
        })
        .eq('id', planId);

      if (error) throw error;
      await fetchPlans();
    } catch (error) {
      console.error('Error toggling plan status:', error);
      alert('Erreur lors de la modification du statut');
    }
  }

  async function syncStripePrice(product: StripeProduct, price: StripePrice) {
    if (!price.unit_amount || !price.recurring) {
      alert('Ce prix n\'est pas valide pour un abonnement');
      return;
    }

    try {
      const { data: existing } = await supabase
        .from('subscription_plans')
        .select('id')
        .eq('stripe_price_id', price.id)
        .maybeSingle();

      const planData = {
        name: `${product.name} - ${price.recurring.interval === 'month' ? 'Mensuel' : 'Annuel'}`,
        stripe_price_id: price.id,
        amount: price.unit_amount,
        currency: price.currency.toUpperCase(),
        interval: price.recurring.interval as 'month' | 'year',
        active: price.active,
      };

      if (existing) {
        const { error } = await supabase
          .from('subscription_plans')
          .update({
            ...planData,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existing.id);

        if (error) throw error;
        alert('Plan synchronisé avec succès');
      } else {
        const { error } = await supabase
          .from('subscription_plans')
          .insert(planData);

        if (error) throw error;
        alert('Plan importé avec succès');
      }

      await fetchPlans();
    } catch (error) {
      console.error('Error syncing Stripe price:', error);
      alert('Erreur lors de la synchronisation du prix');
    }
  }

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Plans d'abonnement</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchStripeProducts()}
            disabled={loadingStripe}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw size={20} className={loadingStripe ? 'animate-spin' : ''} />
            Rafraîchir Stripe
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus size={20} />
            Nouveau plan
          </button>
        </div>
      </div>

      <div className="mb-6 border-b border-gray-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('stripe')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'stripe'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Produits Stripe
          </button>
          <button
            onClick={() => setActiveTab('local')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'local'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Plans locaux ({plans.length})
          </button>
        </nav>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {editingPlan ? 'Modifier le plan' : 'Nouveau plan'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du plan
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Mensuel, Annuel"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ID Prix Stripe
                </label>
                <input
                  type="text"
                  required
                  value={formData.stripe_price_id}
                  onChange={(e) => setFormData({ ...formData, stripe_price_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="price_xxxxxxxxxxxxx"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Trouvez cet ID dans votre tableau de bord Stripe
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prix (EUR)
                </label>
                <input
                  type="number"
                  required
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="29.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Intervalle de facturation
                </label>
                <select
                  value={formData.interval}
                  onChange={(e) => setFormData({ ...formData, interval: e.target.value as 'month' | 'year' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="month">Mensuel</option>
                  <option value="year">Annuel</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="active" className="text-sm font-medium text-gray-700">
                  Plan actif
                </label>
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingPlan ? 'Mettre à jour' : 'Créer'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeTab === 'stripe' ? (
        <div className="space-y-4">
          {loadingStripe ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Chargement des produits Stripe...</p>
            </div>
          ) : stripeProducts.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <p className="text-gray-500">Aucun produit trouvé sur Stripe</p>
              <p className="text-sm text-gray-400 mt-2">Créez des produits sur votre tableau de bord Stripe</p>
            </div>
          ) : (
            stripeProducts.map((product) => (
              <div key={product.id} className="bg-white rounded-lg shadow overflow-hidden">
                <div className="p-4 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{product.name}</h3>
                      {product.description && (
                        <p className="text-sm text-gray-600 mt-1">{product.description}</p>
                      )}
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      product.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {product.active ? <CheckCircle size={12} /> : <XCircle size={12} />}
                      {product.active ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                </div>
                {product.prices.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500 text-center">
                    Aucun prix configuré pour ce produit
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Prix
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Intervalle
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ID Prix
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {product.prices.map((price) => {
                        const isSync = plans.some(p => p.stripe_price_id === price.id);
                        return (
                          <tr key={price.id} className="hover:bg-gray-50">
                            <td className="px-4 py-4 text-sm text-gray-900">
                              {price.unit_amount ? `${(price.unit_amount / 100).toFixed(2)} ${price.currency.toUpperCase()}` : 'N/A'}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-600">
                              {price.recurring
                                ? `${price.recurring.interval_count > 1 ? price.recurring.interval_count + ' ' : ''}${
                                    price.recurring.interval === 'month' ? 'Mois' :
                                    price.recurring.interval === 'year' ? 'An' : price.recurring.interval
                                  }`
                                : 'Paiement unique'}
                            </td>
                            <td className="px-4 py-4 text-sm font-mono text-gray-600">
                              {price.id}
                            </td>
                            <td className="px-4 py-4">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                price.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {price.active ? <CheckCircle size={12} /> : <XCircle size={12} />}
                                {price.active ? 'Actif' : 'Inactif'}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <button
                                onClick={() => syncStripePrice(product, price)}
                                disabled={!price.recurring}
                                className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg text-sm font-medium ${
                                  isSync
                                    ? 'bg-green-50 text-green-700 border border-green-200'
                                    : 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                                title={!price.recurring ? 'Seuls les prix récurrents peuvent être synchronisés' : ''}
                              >
                                <Download size={14} />
                                {isSync ? 'Synchronisé' : 'Importer'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prix
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Intervalle
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID Stripe
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {plans.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    Aucun plan d'abonnement configuré
                  </td>
                </tr>
              ) : (
                plans.map((plan) => (
                  <tr key={plan.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">
                      {plan.name}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">
                      {(plan.amount / 100).toFixed(2)} {plan.currency}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">
                      {plan.interval === 'month' ? 'Mensuel' : 'Annuel'}
                    </td>
                    <td className="px-4 py-4 text-sm font-mono text-gray-600">
                      {plan.stripe_price_id}
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => toggleActive(plan.id, plan.active)}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          plan.active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {plan.active ? <CheckCircle size={12} /> : <XCircle size={12} />}
                        {plan.active ? 'Actif' : 'Inactif'}
                      </button>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(plan)}
                          className="p-1 text-blue-600 hover:text-blue-800"
                          title="Modifier"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(plan.id)}
                          className="p-1 text-red-600 hover:text-red-800"
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
        <h3 className="font-semibold text-gray-900 mb-2">Comment gérer les plans d'abonnement</h3>
        <div className="text-sm text-gray-700 space-y-3">
          <div>
            <p className="font-medium mb-1">Méthode recommandée (via Stripe) :</p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>Créez vos produits et prix dans votre tableau de bord Stripe</li>
              <li>Cliquez sur "Rafraîchir Stripe" pour charger vos produits</li>
              <li>Dans l'onglet "Produits Stripe", cliquez sur "Importer" pour synchroniser un prix</li>
              <li>Le prix apparaîtra dans l'onglet "Plans locaux" et sera disponible pour les abonnements</li>
            </ol>
          </div>
          <div>
            <p className="font-medium mb-1">Méthode manuelle :</p>
            <p className="ml-2">
              Vous pouvez aussi créer des plans manuellement en cliquant sur "Nouveau plan",
              mais vous devrez saisir l'ID du prix Stripe manuellement.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
